import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2 } from "lucide-react";
import { PickerSearch, matchesQuery } from "@/components/admin/picker-search";
import { cn } from "@/lib/utils";
import { apiGet } from "@/lib/api";

interface AssignableSub {
  userId: string;
  name: string;
  email: string;
  companyName: string;
  projects: string[];
}

/** Pick subcontractor workers (grouped by company) from the projects you manage or foreman. */
export function SubWorkerPicker({ selected, onToggle }: { selected: string[]; onToggle: (userId: string) => void }) {
  const [query, setQuery] = useState("");
  const { data, isLoading } = useQuery({
    queryKey: ["sub-assignable"],
    queryFn: () => apiGet<{ data: AssignableSub[] }>("/api/subs/assignable-workers"),
    staleTime: 30_000,
  });
  const rows = (data?.data ?? []).filter((w) => matchesQuery(query, w.name, w.email, w.companyName));
  const companies = [...new Set(rows.map((r) => r.companyName))].sort((a, b) => a.localeCompare(b));

  return (
    <div className="space-y-2">
      <PickerSearch value={query} onChange={setQuery} placeholder="Search subcontractor workers or companies…" />
      <div className="max-h-56 divide-y overflow-y-auto rounded-lg border border-border">
        {isLoading && <p className="p-3 text-center text-xs text-muted-foreground">Loading…</p>}
        {!isLoading && rows.length === 0 && (
          <p className="p-3 text-center text-xs text-muted-foreground">
            No subcontractor workers to assign. They appear here once they are approved and active on one of your projects.
          </p>
        )}
        {companies.map((co) => (
          <div key={co}>
            <p className="bg-muted/40 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{co}</p>
            {rows.filter((r) => r.companyName === co).map((w) => (
              <button
                key={w.userId}
                type="button"
                onClick={() => onToggle(w.userId)}
                className={cn(
                  "flex w-full items-center justify-between px-3 py-2 text-left transition-colors",
                  selected.includes(w.userId) ? "bg-paris-red/8" : "hover:bg-muted/40"
                )}
              >
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium">{w.name}</span>
                  <span className="block truncate text-xs text-muted-foreground">{w.projects.join(", ")}</span>
                </span>
                {selected.includes(w.userId) && <CheckCircle2 className="ml-2 h-4 w-4 shrink-0 text-paris-red" />}
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
